import { IPublicClientApplication } from '@azure/msal-browser';
import { useEffect, useRef } from 'react';
import { useStores } from '../../../common/hooks';
import { aiScrubStreamUrl } from '../../../api/ai-scrubbing';
import { AiScrubScope, BatchProgressSnapshot, BatchProgressStore, isBatchUiTerminal, shouldPollForQueue } from '../../../models';
import { adoptionCandidate, buildDiscoveryKey, shouldDiscover } from './use-ai-scrub-progress-discovery';
import { sessionKey } from './session-key';

// Remove the persisted run id so the effect below cannot resurrect a dead run.
// Callers (e.g. a 404 cancel handler) MUST invoke this BEFORE clearing the store,
// otherwise the effect re-reads the still-present key and restores the run id.
export const clearPersistedBatchRunId = (awardId: number, scope: AiScrubScope = 'awards', countryId?: number): void => {
  sessionStorage.removeItem(sessionKey(awardId, scope, countryId));
  if (scope === 'awards') {
    sessionStorage.removeItem(`aiScrubBatchRunId:${awardId}`); // legacy unscoped key (see migration below)
  }
};

interface UseAiScrubProgressParams {
  awardId: number;
  instance: IPublicClientApplication;
  store: BatchProgressStore;
  scope?: AiScrubScope;
  countryId?: number;
  onTerminal: () => void | Promise<void>;
}

export const useAiScrubProgress = (params: UseAiScrubProgressParams): void => {
  const { awardId, instance, store, scope = 'awards', countryId, onTerminal } = params;
  const { apiStore } = useStores();

  const esRef = useRef<EventSource | undefined>(undefined);
  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const openedRef = useRef<boolean>(false);
  const startedIdRef = useRef<string | undefined>(undefined);
  const finishedRef = useRef<boolean>(false);
  const discoveredKeyRef = useRef<string | undefined>(undefined);

  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  useEffect(() => {
    let cancelled = false;

    const clearPoll = (): void => {
      if (pollRef.current !== undefined) {
        clearInterval(pollRef.current);
        pollRef.current = undefined;
      }
    };

    const closeStream = (): void => {
      if (esRef.current !== undefined) {
        esRef.current.close();
        esRef.current = undefined;
      }
    };

    const finish = async (): Promise<void> => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      closeStream();
      clearPoll();
      sessionStorage.removeItem(sessionKey(awardId, scope, countryId));
      store.setIsBatchStreaming(false);
      await onTerminalRef.current();
    };

    const startPolling = (batchRunId: string): void => {
      if (pollRef.current !== undefined) return;
      pollRef.current = setInterval(async () => {
        const response = scope === 'country-awards'
          ? await apiStore.getCountryAiScrubBatchStatus(instance, awardId, batchRunId)
          : await apiStore.getAiScrubBatchStatus(instance, awardId, batchRunId);
        if (cancelled) return;
        store.setBatchProgress(response.run);
        store.setIsEvaluationRun(response.isEvaluation);
        store.setQueueMetrics(response.queuePosition, response.queueDepth);
        if (isBatchUiTerminal(response.run.state)) {
          await finish();
        }
      }, 5000);
    };

    const start = async (batchRunId: string): Promise<void> => {
      sessionStorage.setItem(sessionKey(awardId, scope, countryId), batchRunId);

      try {
        const status = scope === 'country-awards'
          ? await apiStore.getCountryAiScrubBatchStatus(instance, awardId, batchRunId)
          : await apiStore.getAiScrubBatchStatus(instance, awardId, batchRunId);
        if (!cancelled && store.activeBatchRunId === batchRunId) {
          store.setIsEvaluationRun(status.isEvaluation);
          store.setBatchProgress(status.run);
          store.setQueueMetrics(status.queuePosition, status.queueDepth);

          if (shouldPollForQueue(status.run.state)) {
            startPolling(batchRunId); // poll immediately; it is the only carrier of queue position
          } else {
            clearPoll();
            store.setQueueMetrics(undefined, undefined);
          }

          if (isBatchUiTerminal(status.run.state)) {
            await finish(); // already terminal on load: capture the summary + clear session, skip opening a stream
            return;
          }
        }
      } catch {
        // Best-effort prime so a reload over SSE still shows the eval banner;
        // the poll path sets it too once a status response lands.
      }

      let token: string;
      try {
        ({ token } = scope === 'country-awards'
          ? await apiStore.createCountryAiScrubStreamToken(instance, awardId, batchRunId)
          : await apiStore.createAiScrubStreamToken(instance, awardId, batchRunId));
      } catch {
        if (cancelled || store.activeBatchRunId !== batchRunId) return;
        store.setIsBatchStreaming(false);
        startPolling(batchRunId);
        return;
      }

      if (cancelled || store.activeBatchRunId !== batchRunId) return;

      store.setIsBatchStreaming(true);
      const source = new EventSource(aiScrubStreamUrl(apiStore.baseUrl, awardId, batchRunId, token, scope));
      esRef.current = source;

      source.onopen = () => {
        openedRef.current = true;
      };

      source.onmessage = (event) => {
        const snapshot = JSON.parse(event.data) as BatchProgressSnapshot;
        store.setBatchProgress(snapshot);
        if (shouldPollForQueue(snapshot.state)) {
          startPolling(batchRunId); // idempotent; poll is the only carrier of queue position
        } else {
          clearPoll(); // promoted + SSE healthy -> drop the queued poll; SSE drives counts
          store.setQueueMetrics(undefined, undefined);
        }
        if (isBatchUiTerminal(snapshot.state)) {
          void finish();
        }
      };

      source.onerror = () => {
        // Hard close or failed initial connect: switch fully to polling.
        // Reconnecting (CONNECTING) after a prior open: leave the source to
        // retry natively but start polling as a safety net so the UI keeps
        // advancing even if the stream never recovers.
        if (source.readyState === EventSource.CLOSED || !openedRef.current) {
          source.close();
          if (esRef.current === source) {
            esRef.current = undefined;
          }
        }
        startPolling(batchRunId);
      };
    };

    if (!store.activeBatchRunId) {
      const scopedKey = sessionKey(awardId, scope, countryId);
      let persisted = sessionStorage.getItem(scopedKey);

      if (!persisted && scope === 'awards') {
        const legacyKey = `aiScrubBatchRunId:${awardId}`;
        persisted = sessionStorage.getItem(legacyKey);
        if (persisted) {
          sessionStorage.removeItem(legacyKey);
          sessionStorage.setItem(scopedKey, persisted);
        }
      }

      if (persisted) {
        store.setActiveBatchRunId(persisted);
        return;
      }

      // No local recovery source: fall through (do NOT return) so the cleanup below still
      // registers and keeps `cancelled` live for this discovery closure. One shot per target.
      if (shouldDiscover({ activeBatchRunId: store.activeBatchRunId, persistedId: persisted, lastDiscoveredKey: discoveredKeyRef.current, scope, awardId, countryId })) {
        discoveredKeyRef.current = buildDiscoveryKey(scope, awardId, countryId);
        const discover = async (): Promise<void> => {
          try {
            const latest = scope === 'country-awards'
              ? await apiStore.getLatestCountryAiScrubBatchRun(instance, awardId, countryId!)
              : await apiStore.getLatestAiScrubBatchRun(instance, awardId);
            if (cancelled || store.activeBatchRunId) return;
            const candidate = adoptionCandidate(latest);
            if (candidate) {
              store.setActiveBatchRunId(candidate); // MobX-tracked dep re-runs the effect into start()
            }
          } catch {
            // Best-effort; absence of a badge is the pre-existing behavior.
          }
        };
        void discover();
      }
    }

    const batchRunId = store.activeBatchRunId;
    if (batchRunId && batchRunId !== startedIdRef.current) {
      startedIdRef.current = batchRunId;
      openedRef.current = false;
      finishedRef.current = false;
      void start(batchRunId);
    }

    return () => {
      cancelled = true;
      closeStream();
      clearPoll();
    };
  }, [awardId, instance, store, scope, countryId, apiStore, store.activeBatchRunId]);
};
